/**
 * The birthday list.
 *
 * The thing a club's newsletter has on page two, and the reason
 * migration 0192 put a consent flag on `person` rather than reading
 * `date_of_birth` and printing whatever it found.
 *
 * Two rules, and they are the whole screen:
 *
 *   THE YEAR IS NOT HERE. Not hidden, not formatted away — not in the
 *   response. `fn_birthday_list` returns a month and a day and has no
 *   argument that adds a year. Somebody is going to paste this into a
 *   template, and the only reliable way to keep an age out of a
 *   newsletter is for the age never to arrive.
 *
 *   ONLY PEOPLE WHO SAID YES. Which for most clubs, on the day this
 *   ships, is nobody — the flag defaults to false and has to be ticked
 *   on each member's record. An empty list would read as broken, so the
 *   screen says how many members have a birthday on file and how many
 *   have agreed, and the gap between those two numbers is the work.
 */

import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';
import { clubToday, shiftDate } from '../lib/sheet.ts';

interface Birthday {
  person_id: string;
  name: string;
  birth_month: number;
  birth_day: number;
  /** "March 14". Formatted by the database; there is no year to format. */
  birthday: string;
  membership_number: string | null;
  category_name: string | null;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function BirthdaysPage() {
  const { club } = useSession();
  const zone = club?.timezone;
  const today = clubToday(zone);

  /**
   * A month at a time, or a window.
   *
   * A newsletter is monthly, so a month is the default and the control
   * is a month picker rather than two date fields. The window exists
   * because "the next thirty days" is what somebody planning cards
   * actually wants, and it crosses New Year without any arithmetic
   * here — `fn_birthday_list` handles the wrap.
   */
  const [mode, setMode] = useState<'month' | 'window'>('month');
  const [month, setMonth] = useState(() => Number(today.slice(5, 7)));

  const year = Number(today.slice(0, 4));
  const from =
    mode === 'month' ? `${year}-${String(month).padStart(2, '0')}-01` : today;
  const to =
    mode === 'month'
      ? shiftDate(
          month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`,
          -1,
        )
      : shiftDate(today, 30);

  const list = useApi<{
    birthdays: Birthday[];
    consented: number;
    with_birthday: number;
  }>(`/v1/birthdays?from=${from}&to=${to}`, [from, to]);

  const rows = list.data?.birthdays ?? [];
  const consented = list.data?.consented ?? 0;
  const onFile = list.data?.with_birthday ?? 0;

  // Grouped by month, because a thirty-day window that crosses the end
  // of a month is two lists to a reader even when it is one query.
  const byMonth = new Map<number, Birthday[]>();
  for (const b of rows) {
    if (!byMonth.has(b.birth_month)) byMonth.set(b.birth_month, []);
    byMonth.get(b.birth_month)!.push(b);
  }

  const copyable = rows.map((b) => `${b.birthday} — ${b.name}`).join('\n');

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Birthdays</h1>
          <div className="muted small">
            For the newsletter. The day and the month — never the year.
          </div>
        </div>
        <div className="spacer" />

        <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
          <option value="month">A month</option>
          <option value="window">The next 30 days</option>
        </select>
        {mode === 'month' && (
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        )}
      </div>

      <ErrorBanner error={list.error} />

      <div className="tiles">
        <Tile label="On this list" value={rows.length} />
        <Tile
          label="Members who agreed"
          value={consented}
          note={`of ${onFile} with a birthday on file`}
          tone={consented === 0 ? 'warning' : 'good'}
          to="/members"
        />
      </div>

      {/*
       * The gap between "we know" and "we may print", said plainly.
       *
       * Without this the screen is an empty table on the day it ships and
       * reads as a feature that does not work, when what it actually
       * means is that nobody has been asked yet.
       */}
      {consented < onFile && (
        <Card>
          <Status tone={consented === 0 ? 'warning' : 'neutral'}>
            {onFile - consented} member{onFile - consented === 1 ? ' has' : 's have'} a birthday
            on file and {onFile - consented === 1 ? 'has' : 'have'} not said the club may print
            it. It is a tick box on each member's record, beside the date of birth — nothing
            appears here until somebody has said yes.
          </Status>
        </Card>
      )}

      <Card
        title={
          mode === 'month'
            ? `${MONTHS[month - 1]} birthdays`
            : 'Birthdays in the next thirty days'
        }
        actions={
          rows.length > 0 && (
            <button
              className="secondary small"
              onClick={() => void navigator.clipboard?.writeText(copyable)}
            >
              Copy the list
            </button>
          )
        }
        flush
      >
        {list.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={4} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>
            {consented === 0
              ? 'No member has agreed to appear in the birthday list yet.'
              : 'Nobody who has agreed has a birthday in this period.'}
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Date</th>
                <th>Member</th>
                <th style={{ width: 160 }}>Membership</th>
                <th style={{ width: 180 }}>Category</th>
              </tr>
            </thead>
            <tbody>
              {[...byMonth.entries()].map(([m, entries]) => (
                <Fragment key={m}>
                  {byMonth.size > 1 && (
                    <tr>
                      <td colSpan={4} className="muted small" style={{ background: 'var(--surface-sunk)' }}>
                        {MONTHS[m - 1]}
                      </td>
                    </tr>
                  )}
                  {entries.map((b) => (
                    <tr key={b.person_id}>
                      <td className="muted">{b.birthday}</td>
                      <td>{b.name}</td>
                      <td className="mono muted">{b.membership_number ?? '—'}</td>
                      <td className="muted">{b.category_name ?? '—'}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <p className="muted small">
        A member's date of birth is held because the junior and dependent rules and the club's
        liquor license depend on it. Printing it is a separate permission, asked for on the
        member's own record and dated when it is given. <Link to="/members">The roster</Link> is
        where to grant it.
      </p>
    </>
  );
}
